<template>
    <main class="cookie-policy">
        <h2 class="cookie-policy__title">{{ page.data.title }}</h2>
        <VJoyButton variant="main" icon="cookie" size="small" class="joy-mb-4" @click="openCookies()">
            {{ t('gdpr.cookies.settings') }}
        </VJoyButton>
        <PrismicRichText :field="page.data.description" class="joy-mb-8" />
        <CookieCategory v-for="cat in categoriesWithCookies" :key="cat.id" :category-id="cat.id" />
    </main>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import type {PrismicDocument} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import {createError, useAsyncData} from '#imports';
    import {useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import cookiesData from '~/assets/data/cookies/cookies.json';
    import CookieCategory from '~/components/CookieCategory.vue';
    import {usePrismicDocument} from '~/composables/content/usePrismicDocument';

    const {t} = useTranslation();

    const page = ref();

    type PolicyCookieRow = {name: string; domain: string; category: string; duration: string};
    type CookieMeta = {fetchedAt: string; categories: Array<{id: string; label: string}>};

    const snapshot = cookiesData as {meta: CookieMeta; cookies: PolicyCookieRow[]};

    const categoriesWithCookies = computed(() => snapshot.meta.categories.filter((c) => snapshot.cookies.some((row) => row.category === c.id)));

    const {fetchDocument} = usePrismicDocument('cookiePolicy');
    const {data, error} = await useAsyncData<PrismicDocument>('cookie-policy', () => fetchDocument(), {deep: true});
    if (error.value) {
        throw createError(error.value);
    }
    page.value = data.value!;

    function openCookies() {
        // @ts-expect-error No types for window.openAxeptioCookies
        window.openAxeptioCookies();
    }
</script>

<style lang="scss">
    .cookie-policy {
        .joy-button {
            padding: 0 var(--joy-core-spacing-4);
        }

        &__title {
            font-size: var(--joy-font-size-secondary-200);
            margin-bottom: var(--joy-core-spacing-8);
        }
    }
</style>
